import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, Heart } from 'lucide-react';
import { navLinks } from '../data/mock';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCopy } from '../context/SiteCopyContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Input } from './ui/input';
import SearchPanel from './SearchPanel';

const Header = () => {
  const { count, setIsOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = () => {
    /* Search now handled by SearchPanel component */
  };

  const banner1 = useCopy('topbar.msg_1', 'Free Australia-wide delivery');
  const banner2 = useCopy('topbar.msg_2', '30-day returns');
  const banner3 = useCopy('topbar.msg_3', '5-year warranty');
  const banner4 = useCopy('topbar.msg_4', 'Australian made furniture');

  return (
    <header className="sticky top-0 z-40">
      {/* Announcement marquee */}
      <div className="bg-[#1E140B] text-[#E3D5B8] text-[11px] tracking-[0.22em] uppercase overflow-hidden border-b border-[#33200F]">
        <div className="flex whitespace-nowrap py-2.5 marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0">
              {[banner1, banner2, banner3, banner4].filter(Boolean).map(
                (m, j) => (
                  <span key={j} className="px-8 inline-flex items-center gap-8">
                    <span>{m}</span>
                    <span className="text-[#A07A4F]">⁂</span>
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? 'bg-[#F5EDDD]/95 backdrop-blur-sm shadow-[0_1px_0_rgba(91,58,36,0.08)]' : 'bg-[#F5EDDD]'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-[78px] flex items-center justify-between">
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" className="p-1.5 text-[#33200F]">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#F5EDDD] border-r border-[#C9B596]">
                <SheetHeader>
                  <SheetTitle
                    className="text-[#33200F] text-2xl text-left"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Timber <span className="italic text-[#A07A4F]">&amp;</span> Thread <span className="italic text-[#A07A4F]">co.</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col">
                  {navLinks.map((l) => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={() => setMobileOpen(false)}
                      className="py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide border-b border-[#E3D5B8]"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link
            to="/"
            className="text-[#33200F] text-[12px] lg:text-[13px] xl:text-[15px] tracking-[0.18em] xl:tracking-[0.22em] whitespace-nowrap leading-none uppercase shrink-0"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Timber <span className="italic text-[#A07A4F] tracking-normal normal-case">&amp;</span> Thread <span className="text-[#A07A4F]">Co.</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-8 mx-auto pl-10">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                className={({ isActive }) =>
                  `text-[12px] xl:text-[13px] tracking-[0.14em] xl:tracking-[0.16em] uppercase text-[#33200F] transition-colors hover:text-[#A07A4F] relative ${
                    isActive
                      ? 'after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-px after:bg-[#5B3A24]'
                      : ''
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Icons */}
          <div className="flex items-center gap-2 sm:gap-4 text-[#33200F]">
            <button
              aria-label="Search"
              data-testid="header-search-btn"
              onClick={() => setSearchOpen((s) => !s)}
              className="p-3 hover:text-[#A07A4F] transition-colors"
            >
              <Search className="h-[18px] w-[18px]" strokeWidth={1.4} />
            </button>
            <Link
              to="/wishlist"
              aria-label="Wishlist"
              data-testid="header-wishlist-btn"
              className="p-3 relative hover:text-[#A07A4F] transition-colors"
            >
              <Heart className="h-[18px] w-[18px]" strokeWidth={1.4} />
              {wishCount > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-[#5B3A24] text-[#F5EDDD] text-[10px] font-medium rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1">
                  {wishCount}
                </span>
              )}
            </Link>
            <button
              aria-label="Cart"
              onClick={() => setIsOpen(true)}
              className="p-3 relative hover:text-[#A07A4F] transition-colors"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-[#5B3A24] text-[#F5EDDD] text-[10px] font-medium rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
      </div>
    </header>
  );
};

export default Header;
